import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, Mail, Music, Timer, Zap } from 'lucide-react';
import React, { useState, useEffect } from 'react';

const messages = [
  "Spinning up the extractor...",
  "Analyzing audio waves...",
  "Identifying music segments...",
  "This may take a few moments...",
  "Almost there...",
  "Calibrating the AI model...",
  "Searching for the perfect beat...",
  "Filtering out background noise...",
  "Did you know? The earliest known piece of music is over 4,000 years old.",
  "Hang tight, we're working on it...",
  "Extracting high-fidelity audio...",
  "Just a little longer...",
  "The average song is about 3 minutes long. This might take a bit more.",
  "Processing complex audio layers...",
  "Fine-tuning the results...",
  "Queuing your request...",
];

const MusicNote = () => {
  const x = Math.random() * 200 - 100;
  const y = Math.random() * 100 - 50;
  const scale = Math.random() * 0.5 + 0.5;
  const duration = Math.random() * 5 + 5;
  const delay = Math.random() * 5;

  return (
    <motion.div
      className="absolute"
      style={{ top: '50%', left: '50%', x: `${x}px`, y: `${y}px` }}
      initial={{ opacity: 0, y: 0, scale: 0 }}
      animate={{
        opacity: [0, 0.7, 0],
        y: -200,
        scale: scale,
      }}
      transition={{
        duration: duration,
        delay: delay,
        repeat: Infinity,
        ease: 'linear',
      }}
    >
      <Music className="w-6 h-6 text-white/50" />
    </motion.div>
  );
};

interface LoaderPopupProps {
  onClose: () => void;
  status?: 'loading' | 'queued' | 'error';
  email?: string;
  priority?: string;
  jobId?: string;
  onExtractAnother?: () => void;
}

export default function LoaderPopup({ onClose, status = 'loading', email, priority, jobId, onExtractAnother }: LoaderPopupProps) {
  const [messageIndex, setMessageIndex] = useState(0);

  useEffect(() => {
    if (status !== 'loading') return;
    const interval = setInterval(() => {
      setMessageIndex((prevIndex) => (prevIndex + 1) % messages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [status]);

  return (
    <motion.div
      className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        onClick={(e) => e.stopPropagation()}
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.96 }}
        transition={{ type: 'spring', stiffness: 260, damping: 20 }}
        className="relative w-full max-w-lg"
      >
        {status === 'loading' && (
          <div className="relative flex flex-col items-center justify-center gap-6 bg-gradient-to-br from-purple-600 to-blue-600 rounded-2xl p-8 shadow-2xl overflow-hidden">
            <div className="absolute inset-0 opacity-30">
              {[...Array(5)].map((_, i) => (
                <MusicNote key={i} />
              ))}
            </div>

            <div style={{ perspective: '1000px' }}>
              <motion.div
                style={{ transformStyle: 'preserve-3d' }}
                animate={{ rotateY: 360 }}
                transition={{
                  repeat: Infinity,
                  duration: 10,
                  ease: 'linear',
                }}
                whileHover={{ scale: 1.05, rotateY: 400 }}
                whileTap={{ scale: 0.97, rotateY: 300, rotateX: 20 }}
                className="relative w-40 h-40 cursor-pointer"
              >
                {/* Vinyl Record */}
                <motion.div className="absolute w-full h-full rounded-full bg-gray-900/90 shadow-2xl flex items-center justify-center">
                  {/* Inner Label */}
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-white/80 to-white/40 flex items-center justify-center">
                    <Music className="w-10 h-10 text-gray-900/90" />
                  </div>
                  {/* Spindle Hole */}
                  <div className="absolute w-3 h-3 rounded-full bg-gray-800 border-2 border-gray-700" />
                </motion.div>
                {/* Vinyl Shine */}
                <motion.div
                  className="absolute w-full h-full rounded-full"
                  style={{
                    background:
                      'repeating-conic-gradient(from 0deg, rgba(255,255,255,0.15) 0deg 2deg, transparent 2deg 10deg)',
                  }}
                  animate={{ rotateZ: -360 }}
                  transition={{
                    repeat: Infinity,
                    duration: 20,
                    ease: 'linear',
                  }}
                />
              </motion.div>
            </div>

            <div className="text-center h-12 w-full">
              <AnimatePresence mode="wait">
                <motion.p
                  key={messageIndex}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  transition={{ duration: 0.4 }}
                  className="text-base font-medium text-white"
                >
                  {messages[messageIndex]}
                </motion.p>
              </AnimatePresence>
            </div>
          </div>
        )}

        {status === 'queued' && (
          <div className="relative overflow-hidden rounded-2xl shadow-2xl">
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-500 via-sky-500 to-indigo-600 opacity-90" />
            <div className="relative p-8 text-white space-y-4">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-7 h-7" />
                <h3 className="text-2xl font-semibold">Request Added to Queue</h3>
              </div>
              <p className="text-white/90 leading-relaxed">
                Your extraction request has been securely queued for processing. We will notify you via email as soon as your download link is ready.
              </p>
              {email && (
                <div className="flex items-center gap-2 text-white/95">
                  <Mail className="w-5 h-5" />
                  <span>Notification will be sent to <span className="font-medium">{email}</span>.</span>
                </div>
              )}
              {priority && (
                <div className="flex items-center gap-2 text-white/95">
                  <Timer className="w-5 h-5" />
                  <span>
                    {priority.toLowerCase() === 'paid'
                      ? 'Your request has been prioritized for expedited processing.'
                      : 'Your request is in the standard queue and will be processed shortly.'}
                  </span>
                </div>
              )}

              {priority?.toLowerCase() !== 'paid' && (
                <div className="mt-2 rounded-xl bg-white/15 border border-white/25 p-4">
                  <div className="flex items-start gap-3">
                    <div className="shrink-0 mt-0.5">
                      <Zap className="w-5 h-5" />
                    </div>
                    <div className="space-y-1">
                      <p className="font-semibold">Unlock Priority Processing</p>
                      <p className="text-sm text-white/90">Upgrade to a paid plan to move your requests to the front of the queue and receive results much faster.</p>
                      <div className="pt-2">
                        <a
                          href="/profile"
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-white text-gray-900 font-medium hover:bg-gray-100 transition"
                        >
                          Upgrade now
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {jobId && (
                <div className="bg-white/15 rounded-lg p-3 text-sm break-all">
                  <span className="opacity-90">Tracking ID:</span>
                  <div className="font-mono">{jobId}</div>
                </div>
              )}

              <div className="flex items-center gap-3 pt-2">
                <button
                  onClick={onClose}
                  className="px-4 py-2 rounded-md bg-white text-gray-900 font-medium hover:bg-gray-100 transition"
                >
                  Close
                </button>
                <button
                  onClick={onExtractAnother}
                  className="px-4 py-2 rounded-md bg-black/20 border border-white/30 text-white font-medium hover:bg-black/30 transition"
                >
                  Extract another video
                </button>
              </div>
            </div>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}
